import { useContext } from 'react';
import { GripVertical, Flag, Trash2 } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Checkpoint } from '@/types';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { LangContext } from '@/context/LangProvider';

interface CheckpointRowProps {
  checkpoint: Checkpoint;
  isDisabled: boolean;
  onLabelChange: (checkpointId: string, label: string) => void;
  onDelete: (checkpointId: string) => void;
  // TELV1-421 — react-hook-form field error for this checkpoint's label
  // (e.g. required-label). Surfaces a red ring + message and adds the
  // `validation-error` class so the page can scroll to it on a failed save.
  error?: { message?: string };
  // Clears the label error once a non-empty value is typed (mirrors QuestionBox).
  clearError?: () => void;
}

export function CheckpointRow({
  checkpoint,
  isDisabled,
  onLabelChange,
  onDelete,
  error,
  clearError,
}: CheckpointRowProps) {
  const { lang } = useContext(LangContext);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: checkpoint.id,
    disabled: isDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const hasError = !!error;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-md border border-dashed border-embed-border bg-embed-bg',
        isDragging && 'opacity-60',
        hasError && 'validation-error border-red-400 ring-1 ring-red-300',
      )}
    >
      {/* Drag handle */}
      {!isDisabled && (
        <span {...attributes} {...listeners} className="cursor-grab flex-shrink-0">
          <GripVertical size={16} className="text-embed/50" />
        </span>
      )}

      {/* Checkpoint icon */}
      <Flag size={14} className="flex-shrink-0 text-embed" />

      {/* Inline-editable label + (optional) error message. On a locked project
          (published/completed/viewer) render read-only text — NOT a disabled
          Input, whose disabled:bg-gray-200 grey box clashes with the raspberry
          row. */}
      <div className="flex flex-col flex-1 gap-0.5">
        {isDisabled ? (
          <span className="flex h-7 items-center text-sm text-embed px-0">
            {checkpoint.label || (
              <span className="text-embed/40">{lang.get('msg.checkpointLabelPlaceholder')}</span>
            )}
          </span>
        ) : (
          <Input
            value={checkpoint.label}
            onChange={(e) => {
              onLabelChange(checkpoint.id, e.target.value);
              if (e.target.value.trim() !== '') clearError?.();
            }}
            placeholder={lang.get('msg.checkpointLabelPlaceholder')}
            className="h-7 text-sm border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-embed/40 text-embed px-0"
          />
        )}
        {hasError && (
          <span className="text-xs text-red-500">
            {lang.get(error?.message || 'msg.thisFieldIsRequired')}
          </span>
        )}
      </div>

      {/* Delete button */}
      {!isDisabled && (
        <button
          type="button"
          onClick={() => onDelete(checkpoint.id)}
          className="flex-shrink-0 p-1 rounded hover:bg-embed-bg transition-colors"
          aria-label={lang.get('msg.deleteCheckpoint')}
        >
          <Trash2 size={14} className="text-embed" />
        </button>
      )}
    </div>
  );
}
